import type { ReactNode } from 'react'
import type { StateColor } from '../../live-ui/ui'
import { TXT } from '../../live-ui/ui'
import { t } from '../../i18n'
import { Icon, type IconName } from '../../ui'
import type { RelayJournalEvent } from './api'
import { clock, ms } from './format'

/*
 * Relay events in words (GET /api/admin/relay/journal, SSE journal;
 * design/network-console/proto/src/net/events.tsx). R0 kinds only; an
 * unknown kind shows as its code.
 */

interface EvView {
  icon: IconName
  color: StateColor
  text: ReactNode
}

const Mono = ({ children }: { children: ReactNode }) => <span className="font-mono text-[12px]">{children}</span>

export function describe(e: RelayJournalEvent, nameOf: (id?: string) => string): EvView {
  const d = (e.details ?? {}) as Record<string, unknown>
  const node = <b>{nameOf(e.node_id)}</b>
  const peer = <b>{nameOf(e.peer_node_id)}</b>
  const num = (k: string) => (d[k] === undefined ? undefined : Number(d[k]))
  switch (e.kind) {
    case 'relay_link_down': {
      const reason = String(d.reason ?? 'down').replace(/_/g, ' ')
      return { icon: 'state-fail', color: 'fail', text: <>Link {node} → {peer} down: {reason}.</> }
    }
    case 'relay_link_up':
      return { icon: 'state-live', color: 'live', text: <>Link {node} → {peer} up.</> }
    case 'relay_resume':
      return {
        icon: 'refresh',
        color: 'warn',
        text: (
          <>
            Hop resumed from the ingress ring: outage <Mono>{ms(num('outage_ms'))}</Mono>, <Mono>{String(d.gap_frames ?? 0)}</Mono> frames lost
            {d.from_seq !== undefined && (
              <>
                {' '}
                (from seq <Mono>{String(d.from_seq)}</Mono>)
              </>
            )}
            .
          </>
        ),
      }
    case 'relay_gap':
      return {
        icon: 'state-fail',
        color: 'fail',
        text: (
          <>
            Gap: <Mono>{String(d.gap_frames ?? '?')}</Mono> frames lost
            {d.from_seq !== undefined && (
              <>
                , seq <Mono>{String(d.from_seq)}</Mono>–<Mono>{String(d.to_seq ?? '?')}</Mono>
              </>
            )}{' '}
            (the ring did not cover the outage).
          </>
        ),
      }
    case 'relay_reject':
      return { icon: 'state-warn', color: 'warn', text: <>{node} refused the leg: <Mono>{String(d.code ?? '')}</Mono>{d.reason ? ` · ${String(d.reason)}` : ''}.</> }
    case 'relay_source':
      return { icon: d.live === false ? 'state-idle' : 'state-live', color: d.live === false ? 'idle' : 'live', text: d.live === false ? <>OBS stopped sending to {node}.</> : <>OBS is sending to {node} again.</> }
    case 'route.created':
      return { icon: 'state-live', color: 'live', text: <>Route created, epoch <Mono>e{e.epoch ?? 1}</Mono>.</> }
    case 'route.state':
      return { icon: 'state-start', color: 'start', text: <>Route state: <Mono>{String(d.state ?? '')}</Mono>{d.reason ? ` (${String(d.reason).replace(/_/g, ' ')})` : ''}.</> }
    case 'route.ended':
      return { icon: 'state-idle', color: 'idle', text: <>Route ended{d.reason ? `: ${String(d.reason).replace(/_/g, ' ')}` : ''}.</> }
    case 'node.relay_key_changed':
      return { icon: 'state-idle', color: 'idle', text: <>{node} changed its relay key.</> }
    case 'node.policy_refuse_missing':
      // BILL-B2F: a node without policy_refuse/1 while the center refuses streams by plan.
    {
      const [before, after] = t('node.ev.policyRefuseMissing').split('{node}')
      return { icon: 'state-warn', color: 'warn', text: <>{before}{node}{after}</> }
    }
    case 'node.relay_state':
      return { icon: 'state-start', color: 'start', text: <>{node} relay state: <Mono>{String(d.state ?? '')}</Mono>.</> }
    default:
      return { icon: 'state-idle', color: 'idle', text: <Mono>{e.kind}</Mono> }
  }
}

export function EventList({ events, nameOf, empty = 'No relay events for this route yet.', self }: {
  events: RelayJournalEvent[]
  nameOf: (id?: string) => string
  empty?: string
  /** the node card: events another node reported about this one are marked "reported by" */
  self?: string
}) {
  if (!events.length) return <p className="px-4 py-4 text-sm text-muted">{empty}</p>
  return (
    <ol className="divide-y divide-line" data-testid="route-events">
      {events.map((e) => {
        const v = describe(e, nameOf)
        return (
          <li key={`${e.id}-${e.t}`} data-node={e.node_id} data-peer={e.peer_node_id ?? ''} className="grid grid-cols-[64px_16px_minmax(0,1fr)] gap-x-2 px-4 py-2 text-[13px]" data-kind={e.kind}>
            <span className="tabular pt-px text-[11px] text-muted">{clock(e.t)}</span>
            <span className={`${TXT[v.color]} pt-0.5`} aria-hidden>
              <Icon name={v.icon} size={12} />
            </span>
            <span className="min-w-0 text-text-soft">
              {v.text}
              {self && e.node_id !== self && e.peer_node_id === self && (
                <span className="ml-2 rounded-sm border border-line-2 px-1 text-[11px] text-muted" data-testid="event-as-peer" title="This node is the peer: the other end reported it">
                  reported by {nameOf(e.node_id)}
                </span>
              )}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
